import { bookActions, deployActions } from '@/actions';
import type { BookAppointmentContext } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import { exportIdSlug, isServer, server, setWhitelabelSession, updateDeployTimestamp } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setLang } from '@/locale';
import { languageSchema } from '@/locale/i18n';
import { EmployeeStats, Place, PlaceEmployee, PlaceService } from '@/types/state/shared';
import { useEffect, useReducer, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import HighlightMatch from './components/HighLightMatch';
import { savePlaceVisit, trackPlacePage } from './helpers/tracking';
import placePageReducer from './PlacePage.reducer';

export const usePlacePage = (
  place: Place,
  headerTargetRef: React.MutableRefObject<any>,
  context: BookAppointmentContext,
  isSistaminuten: boolean,
  isWhitelabel: boolean,
) => {
  const location = useLocation();
  const { slugId } = useParams();
  const appDispatch = useAppDispatch();
  const deploy = useAppSelector((state) => state.deploy);
  const [matchedServices, setMatchedServices] = useState<PlaceService[]>([]);
  const searchParams = new URLSearchParams(location.search);

  const [state, dispatch] = useReducer(placePageReducer, {
    search: '',
    showSearch: false,
    searchedServices: [],
    searchedBundles: [],
  });

  useEffect(() => {
    const handleScroll = () => {
      if (headerTargetRef.current) {
        const scrollY = window.scrollY || window.pageYOffset;
        const { offsetTop } = headerTargetRef.current;
        dispatch({ type: 'SET_STICKY', payload: offsetTop - scrollY <= 64.5 });
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [state.isSticky]);

  useEffect(() => {
    const { id, slug } = exportIdSlug(slugId);

    setWhitelabelSession(isWhitelabel);

    if (searchParams.has('lang')) {
      const lang = languageSchema.safeParse(searchParams.get('lang'));
      if (lang.success) setLang(lang.data);
    }

    sessionStorage.setItem('bookSPurl', `/places/${slug}-${id}`);
    sessionStorage.setItem('paymentSPurl', `/places/${slug}-${id}`);

    if (!isWhitelabel) {
      trackPlacePage(place);
    }

    getFirstAvailableTimeServices();

    if (!isServer) {
      savePlaceVisit(place);

      const lastVisitedPlaces = JSON.parse(localStorage.getItem('mpLastVisitedPlaces') || '[]');

      if (!lastVisitedPlaces.includes(place.id)) {
        if (lastVisitedPlaces.length >= 5) {
          lastVisitedPlaces.shift();
        }
        lastVisitedPlaces.push(place.id);
      }

      localStorage.setItem('mpLastVisitedPlaces', JSON.stringify(lastVisitedPlaces));
    }
    if (!isServer) {
      window.scrollTo(0, 0);
      document.querySelector('html').classList.add('scroll-smooth');
    }

    if (deploy) {
      updateDeployTimestamp(deploy);
      appDispatch(deployActions.clearDeployTimestamp());
    }

    return () => {
      if (!isServer) {
        document.querySelector('html').classList.remove('scroll-smooth');
      }
    };
  }, []);

  const getFirstAvailableTimeServices = async () => {
    const shouldHaveFirstAvailableTimes =
      location.state?.fromList && location.state?.searchParams?.q && !(place && place.schedule);

    if (shouldHaveFirstAvailableTimes) {
      const searchParams = Object.fromEntries(Object.entries(location.state.searchParams).filter((param) => param));
      const placeId = place.id;

      const response = await server.request
        .get(`/search${isSistaminuten ? '/deals' : ''}`, { params: { ...searchParams, placeId: placeId } })
        .then(server.handleSuccess)
        .catch(server.handleErrorObject);

      const matchedServices = response?.places?.[0]?.matchedServices;

      if (!Array.isArray(matchedServices)) return null;

      const allServicesHash = {};
      place.services.forEach((category) => {
        if (category.services) {
          category.services.forEach((service) => {
            allServicesHash[service.id] = service;
          });
        }
      });

      const services = matchedServices
        .map((ms) => {
          if (ms.isBundle) {
            return ms;
          } else {
            return {
              ...ms,
              ...allServicesHash[ms.id],
            };
          }
        })
        .filter((s) => !!s);

      setMatchedServices(services);
    } else {
      return null;
    }
  };

  const handleRemoveSearch = () => {
    dispatch({ type: 'REMOVE_SEARCH' });
  };

  const handleShowSearchInput = () => {
    dispatch({ type: 'SHOW_SEARCH_INPUT' });
  };

  const handleOnSearch = (event: React.ChangeEvent<HTMLInputElement>, { newValue }: { newValue?: string }) => {
    dispatch({ type: 'CHANGE_SEARCH_INPUT', value: newValue });
    if (newValue) {
      const filteredBundles = place.bookingBundles
        .filter((bundle) => {
          const bundleName = bundle.name.toLowerCase();
          const searchValue = newValue.toLowerCase();
          return bundleName.includes(searchValue);
        })
        .map((bundle) => {
          return { ...bundle, highlightedName: <HighlightMatch query={newValue} text={bundle.name} /> };
        });
      const filteredServices = place.services
        .flatMap((category) => category.services)
        .filter((service) => {
          const serviceName = service.name.toLowerCase();
          const searchValue = newValue.toLowerCase();

          return serviceName.includes(searchValue);
        })
        .map((service) => {
          return { ...service, highlightedName: <HighlightMatch query={newValue} text={service.name} /> };
        });

      dispatch({ type: 'CHANGE_SEARCHED_SERVICES', payload: { services: filteredServices, bundles: filteredBundles } });
    } else {
      dispatch({ type: 'CHANGE_SEARCHED_SERVICES', payload: { services: [], bundles: [] } });
    }
  };

  const handleOnEmployeeClick = (employee: PlaceEmployee, rating?: EmployeeStats[string]) => {
    dispatch({ type: 'SET_EMPLOYEE_REVIEWS', payload: { employee, rating } });
    return;
  };

  const handleOnCloseEmployeeReviews = () => {
    dispatch({ type: 'REMOVE_EMPLOYEE_REVIEWS' });
  };

  const handleOnPickEmployee = (employee: PlaceEmployee) => {
    context.updateView('pickServices');
    appDispatch(bookActions.clearBook());
    appDispatch(bookActions.removeEmployee());
    appDispatch(bookActions.pickEmployee(employee.id, employee.about.priceListId));
    appDispatch(bookActions.keepEmployee());
    appDispatch(bookActions.addPlace(place));
    context.updateUrlState({ type: 'employee', externalId: employee.about.externalId });
    context.open();
  };

  return {
    matchedServices,
    onRemoveSearch: handleRemoveSearch,
    onShowSearchInput: handleShowSearchInput,
    onSearch: handleOnSearch,
    onEmployeeClick: handleOnEmployeeClick,
    onCloseEmployeeReviews: handleOnCloseEmployeeReviews,
    onPickEmployee: handleOnPickEmployee,
    ...state,
  };
};
